import * as React from "react";
import { observer } from "mobx-react";
import { WizardPackagePWAViewProps, LobCombinations, PackageHiddenSearchDataProps, PackagesSubLobs } from "../typings";
import { FlightHotel } from "../components/FlightHotel";
import { FlightHotelCar } from "../components/FlightHotelCar";
import { FlightCar } from "../components/FlightCar";
import { HotelCar } from "../components/HotelCar";
import { PackageHiddenSearchData } from "../components/PackageHiddenSearchData";
import { SubmitRow } from "src/components/shared/StorefrontWizard/SubmitRow/SubmitRow";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { WizardPackagePWA } from "../WizardPackagePWA";
import { PackageTypeMenuCheckboxes } from "./PackageTypeMenuCheckboxes";
import {
  UitkLayoutGrid,
  UitkLayoutConditionalGridSpan,
  UitkLayoutConditionalGridColumnRow,
} from "uitk-react-layout-grid";
import { PackageErrorState } from "./PackageErrorState";
import { LobErrorSummary } from "src/components/shared/LobErrorSummary/LobErrorSummary";
import { MenuFields } from "./MenuFields";
import { Viewport, ViewSmall, ViewMedium } from "bernie-view";
import { ValuePropSubtitle } from "./ValuePropSubtitle";
import { PackagesStandaloneWizardModulesContext } from "src/components/flexComponents/PageLevelProviders/WizardContextProvider/PackagesStandaloneWizardModulesContext";

export const PackageForm: React.FunctionComponent<WizardPackagePWAViewProps> = observer((props) => {
  const { context, wizardState } = props;
  const {
    packageWizardState,
    packageWizardState: { config: packageConfig },
  } = wizardState;

  const mainGridColumns: UitkLayoutConditionalGridColumnRow = { small: 2, medium: 12 };

  const packageHiddenSearchData: PackageHiddenSearchDataProps = {
    pwaSubLOB: packageWizardState.subLOBState === "" ? props.defaultPackageType : packageWizardState.subLOBState,
    leg1: WizardPackagePWA.getFlightOutLegURLFragment(packageWizardState, packageConfig),
    leg2: packageWizardState.isRoundtripFlight()
      ? WizardPackagePWA.getFlightInLegURLFragment(packageWizardState, packageConfig)
      : undefined,
    flightClass: packageWizardState.flightClassCode,
    config: packageConfig,
    packageWizardState,
  };

  const isNewPackageOnewayFlightType =
    packageWizardState.isDesktop && packageWizardState.experimentNewPackageOnewayFlightType.variant1;
  const isNewPackageMulticityFlightType =
    packageWizardState.isDesktop && packageWizardState.experimentNewPackageMulticityFlightType.variant1;
  const experimentNewPackageVariant1 = isNewPackageOnewayFlightType || isNewPackageMulticityFlightType;
  const displayNewPackageFlightType = packageWizardState.subLOBState === "" && experimentNewPackageVariant1;

  const [lobsMissing, setLobsMissing] = React.useState(0);

  /**
   * Values used if no rehydratation(JS disabled)
   */
  const { noRehydratationValues } = packageConfig.travelers;
  const {
    isFlightCarFormValid,
    isFlightHotelDefaultFormValid,
    isFlightHotelCarFormValid,
    isFlightHotelMultiFormValid,
    isFlightHotelOneWayFormValid,
    isHotelCarFormValid,
    numberOfErrors,
    moreThanOneError,
    errorInputRef,
    subLOBState,
  } = packageWizardState;

  const currentSubLOB = subLOBState || PackagesSubLobs.FLIGHTS_HOTEL;
  const errorSummaryGridColumns: UitkLayoutConditionalGridSpan = { small: 2, medium: 12 };

  const invalidPackageCombination = !Object.values(props.packageSubLOBs.get("lobsAllowed")!).some(
    (val) => val === currentSubLOB
  );
  const selectedLobs = !Object.values(PackagesSubLobs).some((val) => val === currentSubLOB);

  const formHasNoErrors =
    isFlightCarFormValid &&
    isFlightHotelCarFormValid &&
    isFlightHotelDefaultFormValid &&
    isFlightHotelMultiFormValid &&
    isFlightHotelOneWayFormValid &&
    isHotelCarFormValid;

  let lobsMissingErrorState = selectedLobs ? 2 : 0;
  lobsMissingErrorState = invalidPackageCombination && !selectedLobs ? 99 : lobsMissingErrorState;
  React.useEffect(() => setLobsMissing(lobsMissingErrorState), []);

  const showSubHeadingFromFlex = props.flexComponent.model.includePageSubHeading;

  const standaloneSubLOBS = [PackagesSubLobs.HOTELS, PackagesSubLobs.FLIGHTS, PackagesSubLobs.CARS];

  return (
    <PackagesStandaloneWizardModulesContext.Consumer>
      {(singleLOBWizards) => {
        const hasStandaloneWizards = singleLOBWizards.length > 0;
        const isStandaloneWizard = hasStandaloneWizards && standaloneSubLOBS.includes(currentSubLOB as PackagesSubLobs);
        const wizardHotel = singleLOBWizards.filter(({ key }) => key === "wizard-hotel-pwa-v2-1");
        const wizardFlight = singleLOBWizards.filter(({ key }) => key === "wizard-flight-pwa-1");
        const wizardCar = singleLOBWizards.filter(({ key }) => key === "wizard-car-pwa-1");

        const pillsClassName =
          (hasStandaloneWizards && currentSubLOB === "none") || isStandaloneWizard ? "all-t-padding-two" : "";

        return (
          <form
            noValidate={true}
            action={packageConfig.form.action}
            autoComplete="off"
            className="WizardPackagePWA"
            data-testid="wizard"
            onSubmit={packageWizardState.submit}
            id={packageConfig.elementId}
            data-fm={props.flexComponent.fmId}
            data-fm-title-id={props.flexComponent.fmTitleId}
          >
            <>
              {packageConfig.showAllSubLOB && !showSubHeadingFromFlex && <ValuePropSubtitle config={packageConfig} />}
              <UitkLayoutFlex
                space="six"
                className={invalidPackageCombination ? "all-t-margin-zero" : "all-t-padding-two"}
                justifyContent={packageConfig.showAllSubLOB ? "space-between" : "end"}
              >
                {packageConfig.showAllSubLOB && (
                  <PackageTypeMenuCheckboxes
                    className={pillsClassName}
                    lobState={packageWizardState}
                    onSubLOBSelection={props.onSubLOBSelection}
                    defaultPackageType={(packageWizardState.subLOBState as LobCombinations) || props.defaultPackageType}
                    updateLobsMissing={setLobsMissing}
                    minimumLobsNeeded={packageConfig.minimumLOBsNeeded}
                    packageSubLOBs={props.packageSubLOBs}
                    currentSubLOB={currentSubLOB}
                    showOneway={isNewPackageOnewayFlightType}
                    showMulticity={isNewPackageMulticityFlightType}
                    onFlightTypeUpdate={props.onFlightTypeUpdate}
                    setFlightType={props.setFlightType}
                  />
                )}
                {!invalidPackageCombination && !isStandaloneWizard && (
                  <Viewport>
                    <ViewMedium>
                      <>
                        <UitkLayoutFlexItem>
                          <MenuFields
                            currentSubLOB={currentSubLOB}
                            routeStore={props.routeStore}
                            wizardState={props.wizardState}
                            noRehydratationValues={noRehydratationValues}
                            displayNewPackageFlightType={displayNewPackageFlightType}
                            onFlightTypeUpdate={props.onFlightTypeUpdate}
                            setFlightType={props.setFlightType}
                            showOneway={isNewPackageOnewayFlightType}
                            showMulticity={isNewPackageMulticityFlightType}
                            onSelectPreferredClassCode={props.onSelectPreferredClassCode}
                            className="all-b-padding-three"
                          />
                        </UitkLayoutFlexItem>
                      </>
                    </ViewMedium>
                  </Viewport>
                )}
              </UitkLayoutFlex>
            </>

            {!invalidPackageCombination && !isStandaloneWizard && (
              <Viewport>
                <ViewSmall>
                  <MenuFields
                    currentSubLOB={currentSubLOB}
                    routeStore={props.routeStore}
                    wizardState={props.wizardState}
                    noRehydratationValues={noRehydratationValues}
                    displayNewPackageFlightType={displayNewPackageFlightType}
                    onFlightTypeUpdate={props.onFlightTypeUpdate}
                    setFlightType={props.setFlightType}
                    showOneway={isNewPackageOnewayFlightType}
                    showMulticity={isNewPackageMulticityFlightType}
                    onSelectPreferredClassCode={props.onSelectPreferredClassCode}
                  />
                </ViewSmall>
                <ViewMedium />
              </Viewport>
            )}

            {!formHasNoErrors && !isStandaloneWizard && (
              <UitkLayoutGrid columns={mainGridColumns} className="all-y-padding-three" space="three">
                <LobErrorSummary
                  locHeadingToken={props.getLocError!(false)!}
                  locHeadingArgs={moreThanOneError() && [numberOfErrors]}
                  locLinkToken={props.getLocError!(true)}
                  linkClickFocusId="id-roundtrip-error"
                  inputErrorRef={errorInputRef}
                  colSpan={errorSummaryGridColumns}
                  setInputsRefs={props.setInputsRefs}
                />
              </UitkLayoutGrid>
            )}

            {!invalidPackageCombination && currentSubLOB === PackagesSubLobs.FLIGHTS_HOTEL && (
              <FlightHotel
                {...props}
                packageType={PackagesSubLobs.FLIGHTS_HOTEL}
                packageConfig={packageConfig}
                packageWizardState={packageWizardState}
                toggleStaySomewhereElse={props.toggleStaySomewhereElse}
                togglePartialStay={props.togglePartialStay}
                setFlightType={props.setFlightType}
                setInputsRefs={props.setInputsRefs}
              />
            )}

            <UitkLayoutGrid columns={mainGridColumns} space="three">
              {!invalidPackageCombination && currentSubLOB === PackagesSubLobs.FLIGHTS_HOTEL_CAR && (
                <FlightHotelCar
                  {...props}
                  packageType={PackagesSubLobs.FLIGHTS_HOTEL_CAR}
                  packageConfig={packageConfig}
                  packageWizardState={packageWizardState}
                  toggleStaySomewhereElse={props.toggleStaySomewhereElse}
                  togglePartialStay={props.togglePartialStay}
                  setInputsRefs={props.setInputsRefs}
                />
              )}
              {!invalidPackageCombination && currentSubLOB === PackagesSubLobs.FLIGHTS_CAR && (
                <FlightCar
                  {...props}
                  packageType={PackagesSubLobs.FLIGHTS_CAR}
                  packageConfig={packageConfig}
                  packageWizardState={packageWizardState}
                  toggleStaySomewhereElse={props.toggleStaySomewhereElse}
                  togglePartialStay={props.togglePartialStay}
                  setInputsRefs={props.setInputsRefs}
                />
              )}
              {!invalidPackageCombination && currentSubLOB === PackagesSubLobs.HOTEL_CAR && (
                <HotelCar
                  {...props}
                  packageType={PackagesSubLobs.HOTEL_CAR}
                  packageConfig={packageConfig}
                  packageWizardState={packageWizardState}
                  toggleStaySomewhereElse={props.toggleStaySomewhereElse}
                  togglePartialStay={props.togglePartialStay}
                  setInputsRefs={props.setInputsRefs}
                />
              )}
              {invalidPackageCombination && !isStandaloneWizard && (
                <PackageErrorState
                  context={context}
                  lobState={packageWizardState}
                  lobsMissing={lobsMissing}
                  packageWizardState={packageWizardState}
                />
              )}
              <PackageHiddenSearchData {...packageHiddenSearchData} />
            </UitkLayoutGrid>

            {!invalidPackageCombination && !isStandaloneWizard && (
              <SubmitRow lobState={packageWizardState} className="s-t-margin-three" rfrr={"SearchClick_Package"} />
            )}

            {currentSubLOB === PackagesSubLobs.HOTELS && wizardHotel}

            {currentSubLOB === PackagesSubLobs.FLIGHTS && wizardFlight}

            {currentSubLOB === PackagesSubLobs.CARS && wizardCar}
          </form>
        );
      }}
    </PackagesStandaloneWizardModulesContext.Consumer>
  );
});
